import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { CategoryScore } from '../completeness.api';

interface ScoreRingProps {
  overallScore: number;
  categories: CategoryScore[];
  clientId: string;
  isLoading?: boolean;
  productsPath?: string;
  buildProductUrl?: (filters: { categoryId: string }) => string;
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'var(--success-dark)';
  if (score >= 50) return 'var(--warning)';
  return 'var(--destructive)';
}

const RING_SIZE_DESKTOP = 200;
const RING_SIZE_MOBILE = 150;
const STROKE_WIDTH = 20;
const GAP = 4; // gap between segments in degrees

export function ScoreRing({ overallScore, categories, clientId, isLoading, productsPath, buildProductUrl }: ScoreRingProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const segments = useMemo(() => {
    if (!categories.length) return [];

    const totalGap = GAP * categories.length;
    const availableDegrees = 360 - totalGap;
    const perSegment = availableDegrees / categories.length;

    let currentAngle = -90; // start from top
    return categories.map((cat) => {
      const startAngle = currentAngle;
      const endAngle = startAngle + perSegment;
      currentAngle = endAngle + GAP;

      return {
        ...cat,
        startAngle,
        endAngle,
        color: getScoreColor(cat.score),
      };
    });
  }, [categories]);

  if (isLoading) {
    return <ScoreRingSkeleton />;
  }

  const radius = (RING_SIZE_DESKTOP - STROKE_WIDTH) / 2;
  const cx = RING_SIZE_DESKTOP / 2;
  const cy = RING_SIZE_DESKTOP / 2;

  function describeArc(startAngle: number, endAngle: number): string {
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const x1 = cx + radius * Math.cos(startRad);
    const y1 = cy + radius * Math.sin(startRad);
    const x2 = cx + radius * Math.cos(endRad);
    const y2 = cy + radius * Math.sin(endRad);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
  }

  const ariaLabel = `${t('dashboard.overallScore')}: ${Math.round(overallScore)}%. ${categories.map((c) => `${c.categoryName}: ${Math.round(c.score)}%`).join(', ')}`;
  const getCategoryUrl = (categoryId: string) => buildProductUrl?.({ categoryId }) ?? `${productsPath ?? `/data-quality/${clientId}/products`}?category=${categoryId}`;

  return (
    <div className="flex flex-col items-center">
      <div
        className="relative w-[150px] h-[150px] sm:w-[200px] sm:h-[200px]"
        role="img"
        aria-label={ariaLabel}
      >
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${RING_SIZE_DESKTOP} ${RING_SIZE_DESKTOP}`}
          className="score-ring-svg"
        >
          {/* Background track */}
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke="var(--border)"
            strokeWidth={STROKE_WIDTH}
          />

          {/* Category segments */}
          {segments.map((seg, i) => (
            <path
              key={seg.categoryId}
              d={describeArc(seg.startAngle, seg.endAngle)}
              fill="none"
              stroke={seg.color}
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
              className="transition-opacity duration-200"
              tabIndex={0}
              role="button"
              aria-label={`${seg.categoryName}: ${Math.round(seg.score)}%`}
              style={{
                cursor: 'pointer',
                opacity: hoveredIndex === null || hoveredIndex === i ? 1 : 0.4,
                outline: hoveredIndex === i ? '2px solid var(--primary)' : 'none',
                outlineOffset: '2px',
              }}
              onFocus={() => setHoveredIndex(i)}
              onBlur={() => setHoveredIndex(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(getCategoryUrl(seg.categoryId));
                }
              }}
              onMouseEnter={(e) => {
                setHoveredIndex(i);
                const rect = (e.target as SVGElement).closest('svg')?.getBoundingClientRect();
                if (rect) {
                  setTooltipPos({
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top - 10,
                  });
                }
              }}
              onMouseMove={(e) => {
                const rect = (e.target as SVGElement).closest('svg')?.getBoundingClientRect();
                if (rect) {
                  setTooltipPos({
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top - 10,
                  });
                }
              }}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() =>
                navigate(getCategoryUrl(seg.categoryId))
              }
            />
          ))}
        </svg>

        {/* Center score */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center"
          aria-hidden="true"
        >
          <span
            className="text-3xl font-bold"
            style={{ color: getScoreColor(overallScore) }}
          >
            {Math.round(overallScore)}%
          </span>
          <span
            className="text-xs"
            style={{ color: 'var(--foreground-muted)' }}
          >
            {t('dashboard.overallScore')}
          </span>
        </div>

        {/* Tooltip */}
        {hoveredIndex !== null && segments[hoveredIndex] && (
          <div
            className="pointer-events-none absolute z-10 rounded-lg px-3 py-1.5 text-xs font-medium shadow-lg"
            style={{
              left: tooltipPos.x,
              top: tooltipPos.y,
              transform: 'translate(-50%, -100%)',
              backgroundColor: 'var(--foreground)',
              color: 'var(--background)',
            }}
          >
            {segments[hoveredIndex].categoryName}: {Math.round(segments[hoveredIndex].score)}%
          </div>
        )}
      </div>

      {/* Hidden table for screen readers */}
      <table className="sr-only">
        <caption>{t('dashboard.categoryBreakdown')}</caption>
        <thead>
          <tr>
            <th>{t('products.category')}</th>
            <th>{t('products.score')}</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((cat) => (
            <tr key={cat.categoryId}>
              <td>{cat.categoryName}</td>
              <td>{Math.round(cat.score)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScoreRingSkeleton() {
  return (
    <div className="flex items-center justify-center">
      <div
        className="skeleton rounded-full w-[150px] h-[150px] sm:w-[200px] sm:h-[200px]"
        aria-hidden="true"
      />
    </div>
  );
}
